/*Import Estilo*/
import './index.scss';
import '../../css/global.css';
import '../../css/normalize.css';

/*Import Bibliotecas*/
import { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';

/*Import Componentes*/
import Cabecalho from '../../components/Cabecalho';
import Rodape from '../../components/Rodape';
import CookieAceite from '../../components/CookieAceite';

/*Import Contexto*/
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';

/*Import Utils*/
import { formatarPrecoUnitario } from '../../utils/precoUnitario';

/*Import Requisição API*/
import api from '../../api';

export default function Checkout() {
    const previewLoading = false;
    const navigate = useNavigate();
    const { items, clearCart, updateQuantity, removeItem } = useCart();
    const { userId } = useAuth();

    const [enderecos, setEnderecos] = useState([]);
    const [enderecoSelecionado, setEnderecoSelecionado] = useState(null);

    const [texto, setTexto] = useState('');
    const [acaoPosModal, setAcaoPosModal] = useState(null);
    const caixaDeDialogo = useRef(null);
    const caixaDeDialogoExcluir = useRef(null);
    const [processando, setProcessando] = useState(false);
    const [frete, setFrete] = useState(0);
    const [modalExcluirAberto, setModalExcluirAberto] = useState(false);
    const [idItemParaExcluir, setIdItemParaExcluir] = useState(null);
    const [cupomCodigo, setCupomCodigo] = useState('');
    const [cupomAplicado, setCupomAplicado] = useState(null);
    const [descontoAplicado, setDescontoAplicado] = useState(0);
    const [carregandoCupom, setCarregandoCupom] = useState(false);
    const [limiteFreteGratis, setLimiteFreteGratis] = useState(200);
    const [valorEntregaPadrao, setValorEntregaPadrao] = useState(15);

    // Gateway de pagamento
    const [gatewayConfig, setGatewayConfig] = useState(null);
    const pixQrCode = null;
    const pixQrCodeUrl = null;
    const aguardandoPix = false;

    const subtotal = items.reduce((sum, i) => sum + ((Number(i.preco) || 0) * i.quantidade), 0);
    const freteGratis = subtotal >= limiteFreteGratis;
    const faltaFrete = Math.max(limiteFreteGratis - subtotal, 0);
    const progressoFrete = Math.min((subtotal / Math.max(limiteFreteGratis, 1)) * 100, 100);

    useEffect(() => {
        caixaDeDialogo.current = document.getElementById("CaixaDeDialogo");
        caixaDeDialogoExcluir.current = document.getElementById('CaixaDeDialogoExcluirCheckout');
    }, []);

    // Buscar endereços do usuário logado
    useEffect(() => {
        const buscarEnderecos = async () => {
            if (!userId) return;
            try {
                const response = await api.get(`/clientes/${userId}`);
                if (response.data.success && response.data.cliente) {
                    setEnderecos(response.data.cliente.enderecos || []);
                }
            } catch (error) {
                console.error('Erro ao buscar endereços:', error);
            }
        };
        buscarEnderecos();
    }, [userId]);

    useEffect(() => {
        if (enderecoSelecionado === null && enderecos.length > 0) {
            setEnderecoSelecionado(0);
        }
    }, [enderecos, enderecoSelecionado]);

    useEffect(() => {
        const carregarConfigFrete = async () => {
            try {
                const response = await api.get('/frete/config');
                if (response.data?.success) {
                    setLimiteFreteGratis(Number(response.data.limiteFreteGratis || 200));
                    setValorEntregaPadrao(Number(response.data.valorEntregaPadrao || 15));
                }
            } catch (_) {
                setLimiteFreteGratis(200);
                setValorEntregaPadrao(15);
            }
        };

        carregarConfigFrete();
    }, []);

    // Buscar status do gateway de pagamento
    useEffect(() => {
        const buscarGateway = async () => {
            try {
                const response = await api.get('/pagamento/config-status');
                if (response.data.success) {
                    setGatewayConfig(response.data);
                }
            } catch (error) {
                // Gateway não configurado, usando fallback
            }
        };
        buscarGateway();
    }, []);

    const fecharModal = () => {
        if (caixaDeDialogo.current) {
            caixaDeDialogo.current.close();
        }
        if (acaoPosModal) {
            acaoPosModal();
            setAcaoPosModal(null);
        }
    };

    const mostrarModal = () => {
        if (caixaDeDialogo.current) {
            caixaDeDialogo.current.showModal();
        } else {
            console.error('[mostrarModal] caixaDeDialogo.current é null');
        }
    };

    const abrirModalExcluirItem = (idItem) => {
        setIdItemParaExcluir(idItem);
        setModalExcluirAberto(true);
        if (caixaDeDialogoExcluir.current) {
            caixaDeDialogoExcluir.current.showModal();
        }
    };

    const fecharModalExcluirItem = () => {
        if (caixaDeDialogoExcluir.current) {
            caixaDeDialogoExcluir.current.close();
        }
        setModalExcluirAberto(false);
        setIdItemParaExcluir(null);
    };

    const confirmarExcluirItem = () => {
        if (idItemParaExcluir != null) {
            removeItem(idItemParaExcluir);
        }
        fecharModalExcluirItem();
    };

    // Calcular frete via API ou fallback simples
    const [opcoesFreteAPI, setOpcoesFreteAPI] = useState([]);
    const [freteOpcaoSelecionada, setFreteOpcaoSelecionada] = useState(null);
    const [carregandoFrete, setCarregandoFrete] = useState(false);

    const calcularFrete = useCallback(() => {
        if (subtotal >= limiteFreteGratis) return 0;
        if (freteOpcaoSelecionada !== null && opcoesFreteAPI.length > 0) {
            return Number(opcoesFreteAPI[freteOpcaoSelecionada]?.valor || 0);
        }
        const freteCalculado = Math.max(subtotal * 0.1, valorEntregaPadrao);
        return Number(freteCalculado.toFixed(2));
    }, [subtotal, limiteFreteGratis, valorEntregaPadrao, freteOpcaoSelecionada, opcoesFreteAPI]);

    // Buscar opções de frete quando endereço é selecionado
    useEffect(() => {
        const buscarFrete = async () => {
            if (enderecoSelecionado === null) return;
            const endereco = enderecos[enderecoSelecionado];
            if (!endereco?.cep) return;
            setCarregandoFrete(true);

            try {
                const response = await api.post('/frete/calcular', {
                    cepDestino: endereco.cep.replace(/\D/g, '')
                });
                if (response.data.success && response.data.opcoes?.length > 0) {
                    setOpcoesFreteAPI(response.data.opcoes);
                    // Auto-select cheapest option
                    const maisBarato = response.data.opcoes.reduce((min, op, idx) =>
                        op.valor < response.data.opcoes[min].valor ? idx : min, 0
                    );
                    setFreteOpcaoSelecionada(maisBarato);
                }
            } catch (error) {
                // Frete via API indisponível, usando cálculo simplificado
                setOpcoesFreteAPI([]);
            } finally {
                setCarregandoFrete(false);
            }
        };
        buscarFrete();
    }, [enderecoSelecionado, enderecos]);

    // Atualizar frete quando endereço muda
    useEffect(() => {
        if (enderecoSelecionado !== null) {
            const freteCalculado = calcularFrete();
            setFrete(freteCalculado);
        }
    }, [enderecoSelecionado, subtotal, calcularFrete]);

    useEffect(() => {
        if (processando) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [processando]);

    const gerarNumeroPedido = () => {
        const data = new Date();
        const dia = String(data.getDate()).padStart(2, '0');
        const mes = String(data.getMonth() + 1).padStart(2, '0');
        const ano = data.getFullYear();
        const horas = String(data.getHours()).padStart(2, '0');
        const minutos = String(data.getMinutes()).padStart(2, '0');
        const segundos = String(data.getSeconds()).padStart(2, '0');
        const aleatorio = Math.random().toString(36).substring(2, 8).toUpperCase();
        return `PED-${ano}${mes}${dia}-${horas}${minutos}${segundos}-${aleatorio}`;
    };

    const totalFinal = Math.max(Number((subtotal - descontoAplicado + frete).toFixed(2)), 0);
    const validarEndereco = () => enderecos.length > 0 && enderecoSelecionado !== null;
    const exibirTelaProcessando = processando || previewLoading;

    const validarPagamento = () => true;

    const removerCupom = () => {
        setCupomAplicado(null);
        setDescontoAplicado(0);
        setCupomCodigo('');
    };

    const aplicarCupom = async () => {
        const codigo = cupomCodigo.trim().toUpperCase();
        if (!codigo) {
            setTexto('Digite um código de cupom.');
            mostrarModal();
            return;
        }

        setCarregandoCupom(true);
        try {
            const response = await api.post(`/cupom/validar/${codigo}`);

            if (!response.data?.success || !response.data?.data) {
                setTexto(response.data?.message || 'Cupom inválido.');
                mostrarModal();
                return;
            }

            const cupom = response.data.data;
            let desconto = 0;

            if (cupom.tipo_desconto === 'percentage') {
                desconto = (subtotal * Number(cupom.valor_desconto || 0)) / 100;
            } else {
                desconto = Number(cupom.valor_desconto || 0);
            }

            desconto = Math.min(Number(desconto.toFixed(2)), subtotal);

            setDescontoAplicado(desconto);
            setCupomAplicado(cupom.codigo || codigo);
            setCupomCodigo(cupom.codigo || codigo);
            setTexto(`Cupom ${cupom.codigo || codigo} aplicado com sucesso.`);
            mostrarModal();
        } catch (error) {
            const mensagemErro = error.response?.data?.message || 'Erro ao validar cupom.';
            setTexto(mensagemErro);
            mostrarModal();
        } finally {
            setCarregandoCupom(false);
        }
    };

    const finalizarPedido = async () => {
        if (!validarEndereco()) {
            setTexto('Cadastre um endereço no seu perfil antes de finalizar o pedido.');
            mostrarModal();
            return;
        }
        if (!validarPagamento()) return;
        
        // Validar estoque antes de finalizar
        const itensComEstoqueInsuficiente = items.filter(item => 
            Number(item.estoque) <= 0 || item.quantidade > Number(item.estoque)
        );
        
        if (itensComEstoqueInsuficiente.length > 0) {
            const nomesItens = itensComEstoqueInsuficiente.map(item => 
                `${item.nome} (Estoque: ${Number(item.estoque) || 0})`
            ).join(', ');
            setTexto(`Os seguintes itens estão com estoque insuficiente: ${nomesItens}. Atualize o carrinho antes de finalizar o pedido.`);
            mostrarModal();
            return;
        }

        if (gatewayConfig?.ativo && totalFinal <= 1) {
            setTexto('Valor mínimo para pagamento via InfinitePay é maior que R$ 1,00 (ex.: R$ 1,01). Adicione mais itens ao carrinho.');
            mostrarModal();
            return;
        }
        
        setProcessando(true);

        try {
            // Generate order number
            const numPedido = gerarNumeroPedido();

            const endereco = enderecos[enderecoSelecionado];
            const metodo = gatewayConfig?.ativo ? 'InfinitePay Checkout' : 'Pix';
            const statusPagamento = 'Pendente';
            const dataPagamento = null;
            const pedidoData = {
                numero_pedido: numPedido,
                itens: items.map(i => ({
                    id_produto: i.id_produto,
                    id_variacao: i.id,
                    quantidade: i.quantidade,
                    preco: Number(i.preco),
                    descricao_produto: i.nome,
                    url_img: i.imagem_principal
                })),
                desconto: descontoAplicado,
                frete: frete,
                metodo_pagamento: metodo,
                status_pagamento: statusPagamento,
                data_pagamento: dataPagamento,
                endereco_entrega: JSON.stringify(endereco),
                total: totalFinal
            };

            const response = await api.post('/pedidos', {
                pedido: pedidoData,
                clienteId: userId,
                usuarioId: userId,
                enderecoId: endereco?.id || null
            }, { withCredentials: true });

            const pedidoId = response.data?.id_pedido || response.data?.pedidoId;

            if (response.status === 201 && pedidoId) {
                const destinoDetalhes = `/menu/perfil/meuspedidos/${pedidoId}`;
                const redirectRetorno = `${window.location.origin}/pagamento-retorno`;
                const rollbackPedidoCriado = async () => {
                    try {
                        await api.post('/pagamento/rollback-pedido', { pedidoId }, { withCredentials: true });
                    } catch (rollbackError) {
                        console.error('[finalizarPedido] Falha ao remover pedido após erro no checkout:', rollbackError);
                    }
                };

                // Process payment via gateway if active
                if (gatewayConfig?.ativo) {
                    try {
                        const checkoutResponse = await api.post('/pagamento/pix', {
                            pedidoId,
                            valor: totalFinal,
                            descricao: `Pedido ${numPedido} - Fracaro Store`,
                            redirect_url: redirectRetorno
                        }, { withCredentials: true });

                        if (!checkoutResponse.data.success || !checkoutResponse.data.checkout_url) {
                            await rollbackPedidoCriado();
                            setTexto(checkoutResponse.data.message || 'Não foi possível gerar o checkout de pagamento.');
                            mostrarModal();
                            setProcessando(false);
                            return;
                        }

                        await api.put(`/pedidos/${pedidoId}/pagamento`, {
                            valor_pago: 0,
                            status_pagamento: 'Pendente',
                            data_pagamento: null,
                            metodo_pagamento: 'InfinitePay Checkout',
                            usuarioId: userId
                        }, { withCredentials: true });

                        window.location.href = checkoutResponse.data.checkout_url;
                        return;
                    } catch (paymentError) {
                        console.error('[finalizarPedido] Erro no gateway:', paymentError);
                        const payMsg = paymentError.response?.data?.message || 'Erro ao processar pagamento.';
                        await rollbackPedidoCriado();

                        setTexto(`${payMsg} Não foi possível abrir o checkout agora. Nenhum pedido foi criado e seu carrinho foi mantido.`);
                        mostrarModal();

                        setAcaoPosModal(null);
                        setProcessando(false);
                        return;
                    }
                }

                // Fallback: gateway not active, proceed normally
                
                await api.put(`/pedidos/${pedidoId}/pagamento`, {
                    valor_pago: 0,
                    status_pagamento: 'Pendente',
                    data_pagamento: dataPagamento,
                    metodo_pagamento: metodo,
                    usuarioId: userId
                }, { withCredentials: true });

                clearCart();
                
                // Navega direto para a página de detalhes
                navigate(destinoDetalhes);
            } else {
                console.error('[finalizarPedido] Condição falhou - status:', response.status, 'pedidoId:', pedidoId);
            }
        } catch (error) {
            console.error('[finalizarPedido] Erro capturado:', error);
            console.error('[finalizarPedido] error.response:', error.response);
            const errorMessage = error.response?.data?.message || 'Erro ao finalizar pedido. Tente novamente.';
            setTexto(errorMessage);
            mostrarModal();
        } finally {
            setProcessando(false);
        }
    };

    if (items.length === 0) {
        return (
            <section className='container-Principal-Checkout'>
                <Cabecalho />
                <CookieAceite />
                <main>
                    <section className="conteudoMain">
                        <div className="checkout-empty">
                            <div className="empty-left">
                                <h1>Seu carrinho está vazio</h1>
                                <p>Que tal explorar nossos produtos e encontrar algo perfeito para você?</p>
                                <Link className="btn-voltar-compras" to="/produtos">Voltar às compras</Link>
                            </div>
                            <div className="empty-right">
                                <img src="/assets/image/imgTriste.svg" alt="Carrinho vazio - Fracaro Store" />
                            </div>
                        </div>
                    </section>
                </main>
                <Rodape />
            </section>
        );
    }

    return (
        <section className='container-Principal-Checkout'>
            <Cabecalho />
            <CookieAceite />
            <main>
                <section className="conteudoMain">
                    <dialog id="CaixaDeDialogo">
                        <div className='conteudo-dialog'>
                            <div className="dialog-texto">
                                <p>{texto}</p>
                                <button id="botao" onClick={fecharModal}>
                                    Ok
                                </button>
                            </div>
                        </div>
                    </dialog>
                    <dialog id="CaixaDeDialogoExcluirCheckout" className={modalExcluirAberto ? 'open' : ''}>
                        <div className='conteudo-dialog'>
                            <div className="dialog-texto">
                                <p>Você deseja excluir esse item do carrinho?</p>
                                <div className='bnts-decisao'>
                                    <button className="bnt-sim" onClick={confirmarExcluirItem}>
                                        Sim
                                    </button>
                                    <button className="bnt-nao" onClick={fecharModalExcluirItem}>
                                        Não
                                    </button>
                                </div>
                            </div>
                        </div>
                    </dialog>
                    <div className={`checkout-container ${exibirTelaProcessando ? 'checkout-container-processando' : ''}`}>
                        <h1>Checkout</h1>
                        {exibirTelaProcessando ? (
                            <div className="checkout-processando-fullscreen">
                                <div className="loading-produtos">
                                    <svg xmlns='http://www.w3.org/2000/svg' width='36' height='36' viewBox='0 0 24 24' aria-hidden='true'>
                                        <path fill='#131C46' d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z' opacity='0.25' />
                                        <path fill='#131C46' d='M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z'>
                                            <animateTransform attributeName='transform' dur='0.75s' repeatCount='indefinite' type='rotate' values='0 12 12;360 12 12' />
                                        </path>
                                    </svg>
                                    <h2>Você está sendo redirecionado para o Infinity Pay</h2>
                                    <p>Aguarde alguns segundos para abrir o checkout seguro.</p>
                                </div>
                            </div>
                        ) : (
                        <div className="checkout-wrapper checkout-wrapper-single">
                            {/* COLUNA ESQUERDA - ITENS DO PEDIDO FIXO */}
                            <div className="coluna-itens">
                                <div className="dados-pedido">
                                    <div className="cabecalho-pedido">
                                        <h2>
                                            <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M3 8H21M5 3H20C21.1046 3 22 3.89543 22 5V20C22 21.1046 21.1046 22 20 22H5C3.89543 22 3 21.1046 3 20V5C3 3.89543 3.89543 3 5 3Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            Itens do Pedido
                                        </h2>
                                    </div>
                                    <div className="conteudo-pedido">
                                        <div className="itens-carrinho">
                                            {items.map((i, index) => {
                                                const atributos = [];
                                                if (i.cor) atributos.push(i.cor);
                                                if (i.tamanho) atributos.push(i.tamanho);
                                                if (i.tipo) atributos.push(i.tipo);
                                                if (i.material) atributos.push(i.material);
                                                const descricaoAtributos = atributos.join(' / ');

                                                return (
                                                <div key={`${i.id}-${index}`} className="item-carrinho">
                                                    <div className="imagem-produto" onClick={() => navigate(`/produto/${i.id_produto}`)} style={{cursor: 'pointer'}}>
                                                        <img src={i.imagem_principal} alt={i.nome} className="produto-imagem" />
                                                    </div>
                                                    <div className="detalhes-item">
                                                        <p><strong onClick={() => navigate(`/produto/${i.id_produto}`)} style={{cursor: 'pointer'}}>{i.nome}</strong></p>
                                                        <p className="qtd-item">Qtd: {i.quantidade} | {i.unidades_por_pacote} Unit. | {descricaoAtributos}</p>
                                                        <p className="preco-item">R$ {(Number(i.preco)).toFixed(2)} {i.unidades_por_pacote > 1 && (
                                                            <span className="preco-unitario-item"> | Preço Unit.: {formatarPrecoUnitario(i.preco, i.unidades_por_pacote)}</span>
                                                        )}</p>
                                                    </div>
                                                    <div className="subtotal-item">
                                                        <strong>R$ {(Number(i.preco) * i.quantidade).toFixed(2)}</strong>
                                                        <div className="controles-item">
                                                            <button className="bnt-mais" onClick={() => updateQuantity(i.id, +1)}>
                                                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.00065 0.958008C7.57565 0.958008 8.04232 1.42467 8.04232 1.99967V5.95801H12.0007C12.2769 5.95801 12.5419 6.06775 12.7372 6.2631C12.9326 6.45846 13.0423 6.72341 13.0423 6.99967C13.0423 7.27594 12.9326 7.54089 12.7372 7.73624C12.5419 7.93159 12.2769 8.04134 12.0007 8.04134H8.04232V11.9997C8.04232 12.2759 7.93257 12.5409 7.73722 12.7362C7.54187 12.9316 7.27692 13.0413 7.00065 13.0413C6.72438 13.0413 6.45943 12.9316 6.26408 12.7362C6.06873 12.5409 5.95898 12.2759 5.95898 11.9997V8.04134H2.00065C1.72438 8.04134 1.45943 7.93159 1.26408 7.73624C1.06873 7.54089 0.958984 7.27594 0.958984 6.99967C0.958984 6.72341 1.06873 6.45846 1.26408 6.2631C1.45943 6.06775 1.72438 5.95801 2.00065 5.95801H5.95898V1.99967C5.95898 1.42467 6.42565 0.958008 7.00065 0.958008Z" fill="white" /></svg>
                                                            </button>
                                                            <button className="bnt-menos" onClick={() => updateQuantity(i.id, -1)}>
                                                                <svg width="13" height="3" viewBox="0 0 13 3" fill="none" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M1.38932 0H5.34766H11.3893C11.6656 0 11.9305 0.109747 12.1259 0.305097C12.3212 0.500447 12.431 0.765399 12.431 1.04167C12.431 1.31793 12.3212 1.58289 12.1259 1.77824C11.9305 1.97359 11.6656 2.08333 11.3893 2.08333H7.43099H1.38932C1.11306 2.08333 0.848104 1.97359 0.652753 1.77824C0.457403 1.58289 0.347656 1.31793 0.347656 1.04167C0.347656 0.765399 0.457403 0.500447 0.652753 0.305097C0.848104 0.109747 1.11306 0 1.38932 0Z" fill="white" /></svg>
                                                            </button>
                                                            <button className="bnt-excluir" onClick={() => abrirModalExcluirItem(i.id)}>
                                                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.6666 9.16667V14.1667M8.33325 9.16667V14.1667M4.99992 5.83333V15.8333C4.99992 16.2754 5.17551 16.6993 5.48807 17.0118C5.80063 17.3244 6.22456 17.5 6.66659 17.5H13.3333C13.7753 17.5 14.1992 17.3244 14.5118 17.0118C14.8243 16.6993 14.9999 16.2754 14.9999 15.8333V5.83333M3.33325 5.83333H16.6666M5.83325 5.83333L7.49992 2.5H12.4999L14.1666 5.83333" strokeWidth="2.08333" strokeLinecap="round" strokeLinejoin="round" stroke="white"  /></svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                            })}
                                        </div>
                                    </div>
                                    <div className="total-pedido">
                                        <p>Subtotal: <strong>R$ {subtotal.toFixed(2)}</strong></p>
                                        <p>Frete: <strong>{freteGratis ? 'Gratis' : `R$ ${frete.toFixed(2)}`}</strong></p>
                                        <div className="frete-progresso">
                                            <div className="frete-info">
                                                {freteGratis
                                                    ? 'Frete gratis liberado'
                                                    : `Faltam R$ ${faltaFrete.toFixed(2)} para frete gratis`}
                                            </div>
                                            <div className="frete-bar">
                                                <div className="frete-bar-fill" style={{ width: `${progressoFrete}%` }} />
                                            </div>
                                        </div>
                                        {descontoAplicado > 0 && (
                                            <p className="desconto-destaque">
                                                Desconto: <strong>- R$ {descontoAplicado.toFixed(2)}</strong>
                                            </p>
                                        )}
                                        <p className="total-final">Total: <strong>R$ {totalFinal.toFixed(2)}</strong></p>
                                    </div>
                                </div>
                            </div>

                            {/* ENDEREÇO EMBAIXO DOS ITENS */}
                            <div className="coluna-formulario">
                                {/* SEÇÃO DE ENDEREÇO */}
                                    <div className="dados-entrega">
                                    <div className="cabecalho-entrega">
                                        <h2>
                                            <svg width="39" height="31" viewBox="0 0 39 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M26.5 22.1425V5.5C26.5 4.57174 26.1313 3.6815 25.4749 3.02513C24.8185 2.36875 23.9283 2 23 2H5.5C4.57174 2 3.6815 2.36875 3.02513 3.02513C2.36875 3.6815 2 4.57174 2 5.5V21.25C2 21.7141 2.18437 22.1592 2.51256 22.4874C2.84075 22.8156 3.28587 23 3.75 23H4.9925C5.33363 22.2211 5.89434 21.5585 6.60607 21.0932C7.31779 20.6279 8.14968 20.3802 9 20.3802C9.85032 20.3802 10.6822 20.6279 11.3939 21.0932C12.1057 21.5585 12.6664 22.2211 13.0075 23H25.9925C26.1208 22.6908 26.29 22.405 26.5 22.1425ZM26.5 22.1425C26.9548 21.5256 27.5642 21.0396 28.2669 20.7335C28.9695 20.4273 29.7404 20.3119 30.5019 20.3989C31.2633 20.4859 31.9884 20.7721 32.6039 21.2288C33.2194 21.6855 33.7035 22.2964 34.0075 23H37V17.9425C37.0007 16.6726 36.6558 15.4264 36.0025 14.3375L33.85 10.75L32.2575 8.1075C32.1026 7.84626 31.8824 7.62983 31.6185 7.47949C31.3547 7.32914 31.0562 7.25005 30.7525 7.25H26.5V22.1425Z" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M17.75 14.25H9M17.75 9H9M13.375 24.75C13.3758 25.4008 13.2313 26.0436 12.9522 26.6316C12.673 27.2195 12.2662 27.7377 11.7614 28.1485C11.2566 28.5593 10.6665 28.8523 10.0341 29.0062C9.40176 29.16 8.74301 29.1708 8.10592 29.0378C7.46882 28.9048 6.86942 28.6313 6.35141 28.2373C5.83339 27.8433 5.40981 27.3387 5.11153 26.7602C4.81325 26.1818 4.64778 25.5441 4.62719 24.8936C4.6066 24.2431 4.7314 23.5962 4.9925 23C5.39535 22.0802 6.10238 21.3269 6.9949 20.8667C7.88742 20.4064 8.91109 20.2673 9.89409 20.4725C10.8771 20.6777 11.7596 21.2148 12.3934 21.9937C13.0272 22.7726 13.3738 23.7458 13.375 24.75ZM34.375 24.75C34.3758 25.4008 34.2313 26.0436 33.9522 26.6316C33.673 27.2195 33.2662 27.7377 32.7614 28.1485C32.2566 28.5593 31.6665 28.8523 31.0341 29.0062C30.4018 29.16 29.743 29.1708 29.1059 29.0378C28.4688 28.9048 27.8694 28.6313 27.3514 28.2373C26.8334 27.8433 26.4098 27.3387 26.1115 26.7602C25.8132 26.1818 25.6478 25.5441 25.6272 24.8936C25.6066 24.2431 25.7314 23.5962 25.9925 23C26.1208 22.6908 26.29 22.405 26.5 22.1425C26.9548 21.5256 27.5642 21.0396 28.2669 20.7335C28.9695 20.4273 29.7404 20.3119 30.5019 20.3989C31.2633 20.4859 31.9884 20.7721 32.6039 21.2288C33.2194 21.6855 33.7035 22.2964 34.0075 23C34.2525 23.5512 34.3785 24.148 34.375 24.75Z" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            Endereço de Entrega
                                        </h2>
                                    </div>
                                    <div className="conteudo-entrega">
                                        <div className="selecionar-endereco">
                                            <div className="caixas-endereco">
                                                {enderecos.length === 0 ? (
                                                    <p className='nenhum-endereco'>Não há endereços cadastrados.</p>
                                                ) : (
                                                    enderecos.map((end, idx) => (
                                                        <div
                                                            key={end.id || idx}
                                                            className={`endereco-item ${enderecoSelecionado === idx ? 'selecionado' : ''}`}
                                                            onClick={() => setEnderecoSelecionado(idx)}
                                                        >
                                                            <div className='endereco-esquerda'>
                                                                <input
                                                                    type="radio"
                                                                    name="endereco"
                                                                    checked={enderecoSelecionado === idx}
                                                                    onChange={() => setEnderecoSelecionado(idx)}
                                                                />
                                                                <p>
                                                                    {(end.logradouro || end.rua)}, {end.numero}
                                                                    {end.complemento && `, ${end.complemento}`}<br />
                                                                    {end.bairro}, {end.cidade}/{end.estado}<br />
                                                                    CEP {end.cep}
                                                                </p>
                                                            </div>
                                                            <div className='endereco-direita'>
                                                                <div>
                                                                    <button
                                                                        type="button"
                                                                        className='btn-editar-endereco'
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            navigate('/menu/perfil/alterarendereco', { state: { idEndereco: end.id } });
                                                                        }}
                                                                        title="Editar endereço"
                                                                    >
                                                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.4999 5.00007L14.9999 7.50007M10.8333 16.6667H17.4999M4.16659 13.3334L3.33325 16.6667L6.66659 15.8334L16.3216 6.17841C16.634 5.86586 16.8096 5.44201 16.8096 5.00007C16.8096 4.55813 16.634 4.13429 16.3216 3.82174L16.1783 3.67841C15.8657 3.36596 15.4419 3.19043 14.9999 3.19043C14.558 3.19043 14.1341 3.36596 13.8216 3.67841L4.16659 13.3334Z" strokeWidth="2.08333" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                                    </button>
                                                                </div>
                                                                <div className={`endereco-selecionado-icon ${enderecoSelecionado === idx ? 'selecionado' : ''}`}>
                                                                    <svg width="39" height="31" viewBox="0 0 39 31" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M26.5 22.1425V5.5C26.5 4.57174 26.1313 3.6815 25.4749 3.02513C24.8185 2.36875 23.9283 2 23 2H5.5C4.57174 2 3.6815 2.36875 3.02513 3.02513C2.36875 3.6815 2 4.57174 2 5.5V21.25C2 21.7141 2.18437 22.1592 2.51256 22.4874C2.84075 22.8156 3.28587 23 3.75 23H4.9925C5.33363 22.2211 5.89434 21.5585 6.60607 21.0932C7.31779 20.6279 8.14968 20.3802 9 20.3802C9.85032 20.3802 10.6822 20.6279 11.3939 21.0932C12.1057 21.5585 12.6664 22.2211 13.0075 23H25.9925C26.1208 22.6908 26.29 22.405 26.5 22.1425ZM26.5 22.1425C26.9548 21.5256 27.5642 21.0396 28.2669 20.7335C28.9695 20.4273 29.7404 20.3119 30.5019 20.3989C31.2633 20.4859 31.9884 20.7721 32.6039 21.2288C33.2194 21.6855 33.7035 22.2964 34.0075 23H37V17.9425C37.0007 16.6726 36.6558 15.4264 36.0025 14.3375L33.85 10.75L32.2575 8.1075C32.1026 7.84626 31.8824 7.62983 31.6185 7.47949C31.3547 7.32914 31.0562 7.25005 30.7525 7.25H26.5V22.1425Z" stroke="#131C46" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /><path d="M20.207 6.5C20.5323 6.50004 20.8514 6.61919 21.0928 6.8418C21.3354 7.06569 21.4805 7.37842 21.4805 7.71387C21.4804 8.04926 21.3354 8.3621 21.0928 8.58594L12.8838 16.1582C12.3897 16.614 11.6054 16.614 11.1113 16.1582L7.86816 13.166C7.74831 13.0554 7.65034 12.9224 7.58301 12.7725C7.51559 12.6223 7.48047 12.4592 7.48047 12.2939C7.48057 11.9586 7.62552 11.6457 7.86816 11.4219C8.10947 11.1994 8.42873 11.0811 8.75391 11.0811C9.07906 11.0811 9.39832 11.1995 9.63965 11.4219L11.9932 13.5928L19.3213 6.8418C19.5626 6.61921 19.8817 6.5 20.207 6.5Z" fill="#28A745" stroke="#28A745" /><path d="M13.375 24.7503C13.3758 25.4012 13.2313 26.044 12.9522 26.6319C12.673 27.2198 12.2662 27.7381 11.7614 28.1489C11.2566 28.5597 10.6665 28.8527 10.0341 29.0065C9.40176 29.1604 8.74301 29.1712 8.10592 29.0382C7.46882 28.9052 6.86942 28.6317 6.35141 28.2377C5.83339 27.8437 5.40981 27.339 5.11153 26.7606C4.81325 26.1821 4.64778 25.5444 4.62719 24.8939C4.6066 24.2434 4.7314 23.5965 4.9925 23.0003C5.39535 22.0805 6.10238 21.3273 6.9949 20.867C7.88742 20.4068 8.91109 20.2676 9.89409 20.4728C10.8771 20.678 11.7596 21.2152 12.3934 21.9941C13.0272 22.7729 13.3738 23.7462 13.375 24.7503ZM34.375 24.7503C34.3758 25.4012 34.2313 26.044 33.9522 26.6319C33.673 27.2198 33.2662 27.7381 32.7614 28.1489C32.2566 28.5597 31.6665 28.8527 31.0341 29.0065C30.4018 29.1604 29.743 29.1712 29.1059 29.0382C28.4688 28.9052 27.8694 28.6317 27.3514 28.2377C26.8334 27.8437 26.4098 27.339 26.1115 26.7606C25.8132 26.1821 25.6478 25.5444 25.6272 24.8939C25.6066 24.2434 25.7314 23.5965 25.9925 23.0003C26.1208 22.6912 26.29 22.4053 26.5 22.1428C26.9548 21.526 27.5642 21.0399 28.2669 20.7338C28.9695 20.4277 29.7404 20.3123 30.5019 20.3992C31.2633 20.4862 31.9884 20.7725 32.6039 21.2292C33.2194 21.6859 33.7035 22.2968 34.0075 23.0003C34.2525 23.5516 34.3785 24.1483 34.375 24.7503Z" stroke="#131C46" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>

                                            <button
                                                type="button"
                                                className='btn-adicionar-endereco'
                                                onClick={() => navigate('/menu/perfil/cadastroendereco')}
                                            >
                                                + Adicionar novo endereço
                                            </button>
                                        </div>

                                        {/* Opções de Frete */}
                                        {enderecoSelecionado !== null && !freteGratis && (
                                            <div className="opcoes-frete-section">
                                                <h3>Opções de Entrega</h3>
                                                {carregandoFrete ? (
                                                    <p className="carregando-frete">Calculando frete...</p>
                                                ) : opcoesFreteAPI.length > 0 ? (
                                                    <div className="opcoes-frete-list">
                                                        {opcoesFreteAPI.map((opcao, idx) => (
                                                            <label
                                                                key={idx}
                                                                className={`opcao-frete ${freteOpcaoSelecionada === idx ? 'selecionado' : ''}`}
                                                                onClick={() => setFreteOpcaoSelecionada(idx)}
                                                            >
                                                                <input
                                                                    type="radio"
                                                                    name="frete"
                                                                    checked={freteOpcaoSelecionada === idx}
                                                                    onChange={() => setFreteOpcaoSelecionada(idx)}
                                                                />
                                                                <div className="frete-info">
                                                                    <strong>{opcao.nome}</strong>
                                                                    <span>{opcao.prazo}</span>
                                                                </div>
                                                                <span className="frete-valor">
                                                                    {opcao.valor === 0 ? 'Grátis' : `R$ ${Number(opcao.valor).toFixed(2)}`}
                                                                </span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="frete-fallback">Frete: R$ {frete.toFixed(2)}</p>
                                                )}
                                            </div>
                                        )}
                                        {enderecoSelecionado !== null && freteGratis && (
                                            <div className="opcoes-frete-section">
                                                <h3>
                                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M1 3H16V16H1V3Z" stroke="#131C46" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        <path d="M16 8H20L23 11V16H16V8Z" stroke="#131C46" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        <circle cx="5.5" cy="18.5" r="2.5" stroke="#131C46" strokeWidth="2"/>
                                                        <circle cx="18.5" cy="18.5" r="2.5" stroke="#131C46" strokeWidth="2"/>
                                                    </svg>
                                                    Frete Grátis!
                                                </h3>
                                                <p className="frete-gratis-msg">Sua compra se qualifica para frete gratuito.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="dados-cupom">
                                    <div className="cabecalho-cupom">
                                        <h2>
                                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.3334 6.66665L13.3334 7.66665L7.66675 13.3333L6.66675 12.3333L12.3334 6.66665ZM3.33341 3.33331H16.6667C17.5917 3.33331 18.3334 4.07498 18.3334 4.99998V8.33331C17.8914 8.33331 17.4675 8.50891 17.1549 8.82147C16.8423 9.13403 16.6667 9.55795 16.6667 9.99998C16.6667 10.442 16.8423 10.8659 17.1549 11.1785C17.4675 11.4911 17.8914 11.6666 18.3334 11.6666V15C18.3334 15.925 17.5917 16.6666 16.6667 16.6666H3.33341C2.89139 16.6666 2.46746 16.4911 2.1549 16.1785C1.84234 15.8659 1.66675 15.442 1.66675 15V11.6666C2.59175 11.6666 3.33341 10.925 3.33341 9.99998C3.33341 9.55795 3.15782 9.13403 2.84526 8.82147C2.5327 8.50891 2.10878 8.33331 1.66675 8.33331V4.99998C1.66675 4.55795 1.84234 4.13403 2.1549 3.82147C2.46746 3.50891 2.89139 3.33331 3.33341 3.33331ZM3.33341 4.99998V7.11665C3.83981 7.40865 4.26038 7.82886 4.55279 8.33502C4.84521 8.84118 4.99916 9.41543 4.99916 9.99998C4.99916 10.5845 4.84521 11.1588 4.55279 11.6649C4.26038 12.1711 3.83981 12.5913 3.33341 12.8833V15H16.6667V12.8833C16.1603 12.5913 15.7398 12.1711 15.4474 11.6649C15.155 11.1588 15.001 10.5845 15.001 9.99998C15.001 9.41543 15.155 8.84118 15.4474 8.33502C15.7398 7.82886 16.1603 7.40865 16.6667 7.11665V4.99998H3.33341ZM7.91675 6.66665C8.60841 6.66665 9.16675 7.22498 9.16675 7.91665C9.16675 8.60831 8.60841 9.16665 7.91675 9.16665C7.22508 9.16665 6.66675 8.60831 6.66675 7.91665C6.66675 7.22498 7.22508 6.66665 7.91675 6.66665ZM12.0834 10.8333C12.7751 10.8333 13.3334 11.3916 13.3334 12.0833C13.3334 12.775 12.7751 13.3333 12.0834 13.3333C11.3917 13.3333 10.8334 12.775 10.8334 12.0833C10.8334 11.3916 11.3917 10.8333 12.0834 10.8333Z" fill='#fff'/></svg>
                                            Cupom de Desconto
                                        </h2>
                                    </div>
                                    <div className="conteudo-cupom">
                                        <div className="cupom-input-group">
                                            <input
                                                type="text"
                                                placeholder="Digite seu código de cupom"
                                                value={cupomCodigo}
                                                onChange={(e) => setCupomCodigo(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && !cupomAplicado && aplicarCupom()}
                                                disabled={carregandoCupom || !!cupomAplicado}
                                            />
                                            <button
                                                type="button"
                                                className={`btn-aplicar-cupom ${cupomAplicado ? 'btn-remover-estado' : ''}`}
                                                onClick={cupomAplicado ? removerCupom : aplicarCupom}
                                                disabled={carregandoCupom}
                                            >
                                                {carregandoCupom ? '...' : cupomAplicado ? 'Remover' : 'Aplicar'}
                                            </button>
                                        </div>

                                    </div>
                                </div>

                                {/* PIX QR CODE - shown after order is placed with PIX */}
                                {aguardandoPix && (
                                    <div className="pix-qrcode-container">
                                        <div className="pix-header">
                                            <h2>
                                                <svg width="22" height="22" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M30.4859 29.9391C29.732 29.9419 28.9851 29.795 28.2883 29.507C27.5915 29.2191 26.9586 28.7957 26.4261 28.2614L20.5618 22.3861C20.3544 22.1875 20.0785 22.0767 19.7915 22.0767C19.5045 22.0767 19.2286 22.1875 19.0212 22.3861L13.1371 28.2775C12.6049 28.8126 11.9722 29.2368 11.2754 29.5256C10.5785 29.8145 9.83145 29.9622 9.07728 29.9602H7.9292L15.3602 37.4004C16.4747 38.5139 17.9848 39.1393 19.5593 39.1393C21.1337 39.1393 22.6439 38.5139 23.7584 37.4004L31.2042 29.9441L30.4859 29.9391Z" fill="#131C46"/>
                                                    <path d="M9.07852 9.17596C9.8327 9.17395 10.5798 9.32162 11.2766 9.61045C11.9735 9.89928 12.6062 10.3235 13.1383 10.8587L19.0224 16.7513C19.2269 16.9555 19.5039 17.0701 19.7927 17.0701C20.0815 17.0701 20.3585 16.9555 20.5631 16.7513L26.4261 10.881C26.9578 10.3452 27.5904 9.92041 28.2873 9.63134C28.9842 9.34226 29.7315 9.19464 30.4859 9.19705H31.1918L23.746 1.74195C23.1946 1.1897 22.54 0.751616 21.8195 0.452727C21.099 0.153838 20.3268 0 19.5469 0C18.767 0 17.9948 0.153838 17.2743 0.452727C16.5538 0.751616 15.8991 1.1897 15.3478 1.74195L7.9292 9.17596H9.07852Z" fill="#131C46"/>
                                                    <path d="M37.3509 15.365L32.8514 10.8599C32.7499 10.9015 32.6415 10.9233 32.5319 10.9244H30.4859C29.4212 10.9271 28.4006 11.3504 27.646 12.1024L21.783 17.9691C21.255 18.4968 20.5394 18.7932 19.7933 18.7932C19.0473 18.7932 18.3317 18.4968 17.8037 17.9691L11.9184 12.0801C11.164 11.3277 10.1434 10.9038 9.07851 10.9008H6.56684C6.46333 10.8985 6.36104 10.8779 6.26465 10.8401L1.73671 15.365C0.624587 16.4809 0 17.9929 0 19.5693C0 21.1457 0.624587 22.6578 1.73671 23.7737L6.25474 28.2973C6.35092 28.2587 6.45334 28.2382 6.55693 28.2365H9.07851C10.1433 28.2334 11.1639 27.8096 11.9184 27.0573L17.8025 21.1659C18.8663 20.1019 20.7204 20.1019 21.783 21.1659L27.646 27.035C28.4006 27.787 29.4212 28.2103 30.4859 28.213H32.5319C32.6415 28.2138 32.75 28.2356 32.8514 28.2775L37.3509 23.7724C37.9024 23.2204 38.34 22.5649 38.6385 21.8435C38.937 21.1221 39.0906 20.3489 39.0906 19.5681C39.0906 18.7872 38.937 18.014 38.6385 17.2926C38.34 16.5712 37.9024 15.9158 37.3509 15.3637" fill="#131C46"/>
                                                </svg>
                                                Pagamento via PIX
                                            </h2>
                                            <p>Escaneie o QR Code ou copie o código para pagar</p>
                                        </div>
                                        <div className="pix-qr-image">
                                            {pixQrCodeUrl ? (
                                                <img src={pixQrCodeUrl} alt="QR Code PIX" />
                                            ) : (
                                                <div className="pix-qr-text">{pixQrCode}</div>
                                            )}
                                        </div>
                                        <div className="pix-copiar">
                                            <input type="text" readOnly value={pixQrCode} />
                                            <button onClick={() => {
                                                navigator.clipboard.writeText(pixQrCode);
                                                setTexto('Código PIX copiado!');
                                                mostrarModal();
                                            }}>
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <rect x="5" y="5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                                                    <path d="M3 11V3H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                                Copiar
                                            </button>
                                        </div>
                                        <p className="pix-aviso">Após o pagamento, seu pedido será confirmado automaticamente.</p>
                                        <button
                                            className="bnt-avancarCheckout"
                                            onClick={() => navigate(`/menu/perfil/meuspedidos`)}
                                        >
                                            Ver meus pedidos
                                        </button>
                                    </div>
                                )}

                                {!aguardandoPix && (
                                    <div className="bnt-opcoesPedido">
                                            <button className="bnt-finalizarCheckout" onClick={finalizarPedido}>
                                                Continuar para Pagamento
                                            </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        )}
                    </div>
                </section>
            </main>
            <Rodape />
        </section>
    );
}
